import {useCallback, useEffect, useRef} from 'react';

type UseExposeOnDwellProps = {
  dwellMs: number;
  trackExposure: () => void;
};

/**
 * Exposes once the pointer has rested on a trigger for `dwellMs`, so a pointer passing over it
 * doesn't count. Call `startDwell` on enter and `cancelDwell` on leave. Every completed dwell
 * exposes; Eppo dedupes repeats.
 *
 * The timer reads the latest `trackExposure`, so a dwell that starts while the assignment is still
 * 'fallback' exposes the arm it resolves to. Unmounting cancels a pending dwell.
 */
const useExposeOnDwell = ({dwellMs, trackExposure}: UseExposeOnDwellProps) => {
  const dwellTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const latestTrackExposure = useRef(trackExposure);

  useEffect(() => {
    latestTrackExposure.current = trackExposure;
  }, [trackExposure]);

  useEffect(() => () => clearTimeout(dwellTimer.current), []);

  const startDwell = useCallback(() => {
    clearTimeout(dwellTimer.current);
    dwellTimer.current = setTimeout(() => latestTrackExposure.current(), dwellMs);
  }, [dwellMs]);

  const cancelDwell = useCallback(() => {
    clearTimeout(dwellTimer.current);
  }, []);

  return {startDwell, cancelDwell};
};

export default useExposeOnDwell;
